/**
 * La cantidad que se propone al asociar un concepto del catálogo a un elemento del plano, según la
 * MAGNITUD del concepto (que decide la IA por su unidad y descripción: `PlanoMagnitudConcepto`).
 *
 *   IFC (BIM)     → la medida sale DIRECTA del modelo: conteo → piezas, lineal → longitud,
 *                   área → área, volumen → volumen, peso → kg (solo si el concepto se cobra en kg).
 *                   Si el modelo no trae esa magnitud: '' (no se inventa; el usuario la escribe).
 *   DWG (planta)  → conteo/lineal → la base del plano (bloques o largo medido)
 *                   área de PISO  → área de recintos cerrados (m² directo, sin alto)
 *                   área de MURO  → largo × alto (o la suma por plantas, o el neto con vanos)
 *                   volumen       → área de muro × espesor
 *
 * Devuelve '' cuando falta un dato (alto, espesor, escala): el botón Asignar queda bloqueado.
 * Función pura: la usa PlanosPage y la prueban sus tests. NO cambiar el comportamiento sin cambiar
 * también `cantidadParaConcepto.test.ts`.
 */
import type { PlanoCapa, PlanoMagnitudConcepto } from './domain.js';
import { unitsAreEquivalent } from './units.js';
import { medidaPrincipalIfc } from './etiquetasIfc.js';

type Medidas = NonNullable<PlanoCapa['medidas']>;

export interface EntradaCantidadParaConcepto {
  /** Magnitud del concepto (la IA). undefined = aún sin clasificar; null = la IA no pudo decidir. */
  magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined;
  /** Unidad del concepto en el catálogo ("kg", "m2"…). Solo importa para el peso. */
  unidadConcepto?: string | null;
  /** La base del elemento: conteo de bloques, largo medido de la capa, o la medida IFC por defecto. */
  cantidadDefault: number;
  /**
   * La base es un CONTEO (bloques). false = es un largo (capa del plano): un concepto por pieza no la
   * toma (eran 69.48 m de línea de puertas entrando como 69.48 puertas). undefined = como antes.
   */
  baseEsConteo?: boolean;
  /** Medidas del modelo IFC. Presente = el elemento es de un IFC. */
  medidas?: Medidas | null;
  /** IFC: la medida que manda según el inventario (`capa.medidas.manda`); reemplaza la de `medidas`. */
  mandaIfc?: Medidas['manda'];
  /** DWG: área de recintos cerrados medida (m²). */
  areaM2?: number | null;
  /** DWG: el plano no tiene unidad resuelta. */
  sinEscala?: boolean;
  /** DWG: área de muro sumada planta por planta (cada una con su alto), si se pudo. */
  areaMuroPorPlantas?: number | null;
  /** DWG: neto del muro con vanos descontados (`cuentaDeMuroConVanos(...).netoM2`), si se pudo. */
  netoVanosM2?: number | null;
  /**
   * Hay un concepto ELEGIDO. Con concepto elegido y la magnitud aún desconocida (cargando, o la IA no pudo decidir) no hay
   * una cantidad segura que proponer en un dibujo: el largo de la capa NO es la cantidad de un concepto por m² (entraba
   * 33.31 en vez de 89.94). Vacío → el usuario la escribe. Sin concepto elegido (undefined/false) todo sigue como antes.
   */
  conceptoElegido?: boolean;
  /** Lo que escribió el usuario (acepta coma decimal). */
  altoStr: string;
  espesorStr: string;
}

/**
 * ¿El área es de PISO/LOSA (horizontal, sale directa del plano) o de MURO (largo × alto)? La IA lo
 * dice con `inputExtra`: si NO pide alto, es horizontal. Exige además que la capa tenga área medida.
 */
export function esAreaDePiso(
  magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined,
  areaM2: number | null | undefined,
): boolean {
  return magnitud?.magnitud === 'area' && magnitud.inputExtra === 'ninguno' && (areaM2 ?? 0) > 0;
}

export function cantidadParaConcepto(e: EntradaCantidadParaConcepto): string {
  const base = e.cantidadDefault;
  const alto = Number(e.altoStr.replace(',', '.'));
  const espesor = Number(e.espesorStr.replace(',', '.'));
  const mag = e.magnitud?.magnitud;
  if (e.medidas) {
    const md = e.medidas;
    if (mag === 'conteo') return String(md.conteo);
    // Si el modelo no trae esa magnitud para el tipo (IFC sin cantidades embebidas, muy común), no se
    // inventa: antes caía al conteo y un concepto por m² tomaba como cantidad el NÚMERO de elementos.
    if (mag === 'lineal') return md.longitud != null ? String(md.longitud) : '';
    if (mag === 'area') return md.area != null ? String(md.area) : '';
    if (mag === 'volumen') return md.volumen != null ? String(md.volumen) : '';
    // Peso: el modelo pesa en kg. En otra unidad (ton…) no se convierte a escondidas.
    if (mag === 'peso') {
      return md.peso != null && md.peso > 0 && unitsAreEquivalent(e.unidadConcepto, 'kg') ? String(md.peso) : '';
    }
    // Aún sin concepto (o magnitud 'otro'): la medida que MANDA en ese tipo.
    const mp = medidaPrincipalIfc({ ...md, manda: e.mandaIfc });
    return mp ? String(mp.valor) : String(base);
  }
  // Concepto elegido pero sin saber cómo se mide (cargando o la IA falló): no se propone el largo de la capa como cantidad.
  if (e.conceptoElegido && mag === undefined) return '';
  if (esAreaDePiso(e.magnitud, e.areaM2)) return String(e.areaM2);
  if (e.sinEscala) return ''; // sin unidad del plano no hay medida
  // Un plano 2D no trae peso: un concepto en kg NO puede tomar el largo como si fueran kilos (era
  // el bug: 112 m de muro entraban como 112 kg). Vacío → el usuario escribe la cantidad.
  if (mag === 'peso') return '';
  // Concepto por pieza sobre un largo: no hay cuántas piezas; vacío → el usuario la escribe.
  if (mag === 'conteo' && e.baseEsConteo === false) return '';
  // Muro con vanos y TODOS sus altos: bruto − Σ vanos. Mientras falte algún alto, queda el cálculo
  // de siempre (eje neto × alto = vanos de piso a techo) y el panel avisa.
  const netoVanos = e.netoVanosM2 ?? null;
  if (netoVanos != null && (mag === 'area' || mag === 'volumen')) {
    if (mag === 'area') return String(Math.round(netoVanos * 100) / 100);
    return espesor > 0 ? String(Math.round(netoVanos * espesor * 1000) / 1000) : '';
  }
  const areaPlantas = e.areaMuroPorPlantas ?? null;
  if (mag === 'area') {
    if (areaPlantas != null) return String(areaPlantas);
    return alto > 0 ? String(Math.round(base * alto * 100) / 100) : '';
  }
  if (mag === 'volumen') {
    if (areaPlantas != null) return espesor > 0 ? String(Math.round(areaPlantas * espesor * 1000) / 1000) : '';
    return alto > 0 && espesor > 0 ? String(Math.round(base * alto * espesor * 1000) / 1000) : '';
  }
  return String(base);
}

/**
 * ¿La cantidad de muro (m² o m³) descansa en un hueco descontado de piso a techo porque le falta su
 * alto? Entonces el renglón entra 'revisar' (mismo mecanismo que un complementario con
 * `dimensionesFaltantes`): la cuenta usa un supuesto que el plano no dice. No aplica a conteo/lineal,
 * al área de piso, sin escala ni a un elemento sin huecos.
 */
export function cantidadDeMuroSinAltoDeVano(e: {
  magnitud: EntradaCantidadParaConcepto['magnitud'];
  areaM2?: number | null;
  sinEscala?: boolean;
  /** `cuentaDeMuroConVanos(...).tiposSinAlto`; null/undefined = el elemento no tiene huecos. */
  tiposVanosSinAlto?: number | null;
}): boolean {
  const mag = e.magnitud?.magnitud;
  if (mag !== 'area' && mag !== 'volumen') return false;
  if (esAreaDePiso(e.magnitud, e.areaM2) || e.sinEscala) return false;
  return (e.tiposVanosSinAlto ?? 0) > 0;
}
